import type { PlatformRole } from '@/lib/api/contract';
import type { UserRole } from '@/types';
import { ROLES_QUE_ADMINISTRAN } from './equipo';

/**
 * Si quien mira puede cambiar el trabajo del día: asignar, responder, anotar,
 * llamar, poner en espera, resolver.
 *
 * ES EL ESPEJO DE `WRITE_ROLES` del servidor (`backend/api/v2/access.py`), y
 * desde el 2026-09-21 tiene que mirar **las dos cosas por separado**, no una:
 *
 * - `'support'` NO escribe. Es el rol que lee todas las organizaciones y las
 *   configura sin tocar un solo registro del cliente, y `WRITE_ROLES` no lo
 *   nombra: ofrecerle «Responder» o «Anotar» sería dibujarle un botón que
 *   siempre contesta 403.
 * - `'super'` escribe siempre, en cualquier organización. Es `platform_admin`,
 *   que sí está en `WRITE_ROLES`.
 * - El cliente escribe si no es de sólo lectura.
 *
 * ANTES ERA UN `||` sobre un booleano «es personal de Ribox», y ese atajo es
 * exactamente lo que le habría dado al soporte permiso de editar registros: un
 * booleano no distingue a los dos roles nuestros. El cortocircuito se fue a
 * propósito y hay una prueba que lo sostiene (`permisos.test.ts`).
 *
 * Módulo PURO.
 */
export const puedeEscribir = (role: UserRole, platformRole: PlatformRole): boolean => {
  if (platformRole === 'support') return false;
  if (platformRole === 'super') return true;
  return role !== 'viewer';
};

/**
 * Si quien mira ADMINISTRA la organización: lo destructivo del trabajo diario.
 *
 * ES EL ESPEJO DE `ADMIN_ROLES` del servidor (`backend/api/v2/access.py`), que
 * es la compuerta de borrar una empresa, un negocio y —desde el 2026-10-09— una
 * persona con todo lo suyo. Los mismos tres casos que `puedeEscribir`, un piso
 * más arriba:
 *
 * - `'support'` NO. Lee y configura todas las organizaciones y no toca un solo
 *   registro del cliente; `ADMIN_ROLES` une `PLATFORM_SUPER` y no al soporte.
 * - `'super'` sí, en cualquier organización.
 * - Del cliente, sólo su Administrador (`ROLES_QUE_ADMINISTRAN`).
 *
 * Acá sólo se decide qué se DIBUJA: quien llegue igual a la puerta sin el rol
 * recibe el 403 del servidor, con su frase.
 */
export const puedeAdministrar = (role: UserRole, platformRole: PlatformRole): boolean => {
  if (platformRole === 'support') return false;
  if (platformRole === 'super') return true;
  return ROLES_QUE_ADMINISTRAN.includes(role);
};

/** Quien mira, con lo que la sesión ya sabe de él (`useEntitlements`). */
export interface QuienMira {
  /** `user.id`: el mismo id con que el servidor firma la nota en `actorId`. */
  id: string;
  role: UserRole;
  /** `platformRole` de la sesión: de qué lado de Ribox está, si es de alguno. */
  platformRole: PlatformRole;
}

/**
 * Lo que la regla mira de una entrada de la línea de tiempo: qué es, quién la
 * firmó y si quien la firmó es una persona. Una `Activity` lo trae tal cual; un
 * evento de la persona lo arma `entradaDeEventoDePersona`.
 */
export interface EntradaDeLaLinea {
  type: string;
  actorId: string;
  actorType: string;
}

/** Lo que quien mira puede hacerle a una entrada de la línea de tiempo. */
export interface AccionesSobreNota {
  editar: boolean;
  borrar: boolean;
}

export const SIN_ACCIONES: AccionesSobreNota = { editar: false, borrar: false };

/**
 * Si quien mira puede corregir o borrar esta entrada de la línea de tiempo.
 *
 * SÓLO UNA NOTA ESCRITA POR UNA PERSONA. Una llamada, un mensaje o un cambio de
 * etapa que registró el sistema son el registro de algo que pasó, y la nota que
 * deja una automatización también es `note` pero no la escribió nadie
 * (`actorType` no es `user`). El servidor rechaza todo eso con 422, y ofrecer
 * «Editar» ahí sería prometer lo que la API no hace.
 *
 * LO QUE ALGUIEN REGISTRÓ A MANO CON OTRO TIPO QUEDA AFUERA, POR AHORA. Desde el
 * 2026-10-04 una persona también anota llamadas, correos, reuniones y WhatsApp
 * (`lib/actividad-registrada.ts`), y el servidor deja a su autor corregirlos y
 * borrarlos como a una nota. Esta regla todavía no los ofrece en la línea del
 * embudo: es más angosta que la del servidor, nunca más ancha, así que nada de
 * lo que ofrece termina en un rechazo. En la historia de la persona ese mismo
 * hecho ES una `note`, y ahí sí lleva su menú.
 *
 * LA CORRIGE Y LA BORRA QUIEN LA ESCRIBIÓ, y nadie más la corrige: el nombre que
 * firma una nota es de quien la escribió, y un texto cambiado por otro seguiría
 * saliendo con su firma. Una nota sin autor —las del CRM viejo— no la corrige
 * nadie.
 *
 * QUIEN ADMINISTRA LA ORGANIZACIÓN BORRA LA DE CUALQUIERA —una nota escrita en la
 * ficha equivocada, o de alguien que ya no está—: el Administrador del cliente y
 * el super de Ribox. El soporte no, y ni siquiera llega hasta acá: `puedeEscribir`
 * ya lo devolvió sin acciones dos líneas antes.
 *
 * QUIEN SÓLO MIRA NO HACE NINGUNA DE LAS DOS, ni con una nota suya de cuando
 * escribía: es el piso de escritura del servidor, el mismo `puedeEscribir`.
 *
 * Módulo PURO: lo usan la línea de tiempo para decidir qué ofrece y el backend
 * simulado para contestar lo que contesta el servidor.
 */
export function accionesSobreNota(entrada: EntradaDeLaLinea, quien: QuienMira): AccionesSobreNota {
  if (entrada.type !== 'note' || entrada.actorType !== 'user') return SIN_ACCIONES;
  if (!puedeEscribir(quien.role, quien.platformRole)) return SIN_ACCIONES;
  if (entrada.actorId !== '' && entrada.actorId === quien.id) return { editar: true, borrar: true };
  const administra = quien.platformRole === 'super' || ROLES_QUE_ADMINISTRAN.includes(quien.role);
  return { editar: false, borrar: administra };
}
